// Helpers for danger reports (sectionsDangers): labels, sorting and the "à reconfirmer" flag.
import { dangerCanStop, dangerImpacts, dangerTypes } from "@/store/db/dangers.db";
import { isoDateToDate } from "@/utils/dates";

// An active danger without a check-up for this many days is flagged "à reconfirmer". It is never archived automatically.
export const DANGER_RECONFIRM_DAYS = 30;
export const DANGER_MAX_PHOTOS = 4;
export const DANGER_PHOTO_MAX_SIZE = 1600;

const DAY_MS = 24 * 60 * 60 * 1000;

function labelFor(choices, value) {
  const choice = choices.find((item) => item.value === value);
  return choice ? choice.text : "";
}

export function dangerTypeLabel(danger) {
  if (!danger) return "";
  if (danger.type === "autre") {
    return (danger.type_other || "").trim() || labelFor(dangerTypes, "autre");
  }
  return labelFor(dangerTypes, danger.type);
}

export function dangerImpactLabels(danger) {
  const impacts = Array.isArray(danger && danger.impacts) ? danger.impacts : [];
  // Keep the order of the choices, not the order they were checked in.
  return dangerImpacts.filter((item) => impacts.includes(item.value)).map((item) => item.text);
}

export function dangerCanStopLabel(danger) {
  if (!danger) return "";
  if (danger.can_stop === "autre") {
    return (danger.can_stop_other || "").trim() || labelFor(dangerCanStop, "autre");
  }
  return labelFor(dangerCanStop, danger.can_stop);
}

// One line describing the danger, e.g. "Rapide du Moulin : Arbre dans la rivière (Bloque la ligne de gauche)".
export function dangerSummary(danger) {
  const impacts = dangerImpactLabels(danger);
  const what = impacts.length
    ? `${dangerTypeLabel(danger)} (${impacts.join(", ").toLowerCase()})`
    : dangerTypeLabel(danger);
  const rapid = ((danger && danger.rapid_name) || "").trim();
  return rapid ? `${rapid} : ${what}` : what;
}

// "2026-07-01" -> "1 juillet 2026"
export function formatDangerDate(isoDate) {
  const date = isoDateToDate(isoDate);
  return date ? date.toLocaleDateString("fr-CA", { day: "numeric", month: "long", year: "numeric" }) : "";
}

// Date of the last confirmation that the danger is there: the last check-up, else the start date ("YYYY-MM-DD").
export function lastConfirmedDate(danger) {
  if (!danger) return null;
  return (danger.last_checkup && danger.last_checkup.date) || danger.start_date || null;
}

export function needsReconfirmation(danger, today = new Date()) {
  if (!danger || danger.archived) return false;
  const confirmed = isoDateToDate(lastConfirmedDate(danger));
  if (!confirmed) return true;
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.round((startOfToday - confirmed) / DAY_MS) > DANGER_RECONFIRM_DAYS;
}

// Most recent first: start date, then creation time for dangers that started the same day.
export function sortDangers(dangers) {
  const createdAt = (danger) => {
    const value = danger.created_at;
    if (!value) return 0;
    return typeof value.toMillis === "function" ? value.toMillis() : new Date(value).getTime() || 0;
  };
  return [...dangers].sort(
    (a, b) => (b.start_date || "").localeCompare(a.start_date || "") || createdAt(b) - createdAt(a),
  );
}

export function splitDangers(dangers) {
  const sorted = sortDangers(Array.isArray(dangers) ? dangers : []);
  return {
    active: sorted.filter((danger) => !danger.archived),
    archived: sorted.filter((danger) => danger.archived),
  };
}

// Active dangers of several sections (the /dangers page): one group per section, the most recent danger first.
export function groupDangersBySection(dangers) {
  const groups = new Map();
  sortDangers(Array.isArray(dangers) ? dangers : []).forEach((danger) => {
    if (!groups.has(danger.section_id)) {
      groups.set(danger.section_id, {
        sectionId: danger.section_id,
        alias: danger.section_alias,
        river: danger.river,
        name: danger.section_name,
        dangers: [],
      });
    }
    groups.get(danger.section_id).dangers.push(danger);
  });
  return [...groups.values()];
}
